import React, { useState, useEffect } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
  Snackbar
} from '@mui/material';
import {
  Language,
  Save,
  ArrowBack,
  Launch,
  Analytics,
  Settings,
  People,
  Assignment,
  Extension,
  CheckCircle,
  Error,
  Refresh,
  TableChart,
  History,
  VerifiedUser,
  Warning,
  Payment,
  DateRange,
  Build,
  Article,
  Public,
  Business,
  ContactMail,
  Subscriptions,
  Edit,
  Delete,
  Add
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import api from '../services/api';
import LogsPanel from '../components/LogsPanel';

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const SiteEdit = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [checkingPlugin, setCheckingPlugin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabMap = { details: 0, contacts: 1, subscriptions: 2, domains: 3 };
    return tabMap[params.get('tab')] || 0;
  });
  const [verifyingSheet, setVerifyingSheet] = useState(false);
  const [originalSheetId, setOriginalSheetId] = useState('');
  const [sheetVerified, setSheetVerified] = useState(true);
  const [verificationError, setVerificationError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [domains, setDomains] = useState([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [tabCounts, setTabCounts] = useState({ contacts: null, subscriptions: null, domains: null });
  const [domainDialogOpen, setDomainDialogOpen] = useState(false);
  const [domainDialogMode, setDomainDialogMode] = useState('add');
  const [editingDomain, setEditingDomain] = useState(null);
  const [domainFormData, setDomainFormData] = useState({
    domain_name: '', is_primary: false,
    cloudflare_api_token: '', hosting_expiry_date: '', notes: '',
    final_url: '', hosting_provider: '', dns_provider: '', cms_detected: '', registrar: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDomain, setDeletingDomain] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [scanningDomainId, setScanningDomainId] = useState(null);

  const [formData, setFormData] = useState({
    // Basic fields
    rootdomain: '',
    website_url: '',

    // Account info
    account_name: '',
    account_status: '',
    priority: '',
    total_spend: '',
    doctor_name: '',
    account_id: '',
    account_id_unique: '',
    item_id: '',

    // Status & Classification (NEW)
    engagement_level: '',
    industry: '',
    account_source: '',

    // Team assignments
    assigned_cs: '',
    cs: '',
    strategist: '',
    writer: '',
    seo_shirt_size: '',
    seo_hours: '',
    monthly_update_hours: '',

    // Billing (NEW)
    quickbooks_account: '',
    phone: '',
    billing_address: '',
    billing_city: '',
    billing_state: '',
    billing_postal_code: '',

    // Business Review & Lifecycle (NEW)
    br_status: '',
    br_date: '',
    start_date: '',
    cancellation_notification_date: '',
    cancellation_date: '',
    opportunity_count: '',

    // Services Notes (NEW)
    services_notes: '',

    // Technical (NEW from Access List)
    cms: '',
    divi: '',
    domain_hosted_by_nkp: '',
    hosting_details: '',
    preferred_url: '',
    looker_studio: '',

    // Analytics
    ga4_id: '',
    ga4_email: '',
    ga4_account_name: '',
    ga4_property_id: '',
    ga4_property_name: '',
    ga4_star: '',
    gtm_id: '',
    gtm_email: '',

    // Search Console
    gsc_domain: '',
    gsc_prefix: '',
    gsc_url_prefix: '',
    verified_gsc_prefix: '',
    verified_url_prefix: '',
    gsc_email: '',

    // Domain (NEW)
    registrar: '',
    registrar_username: '',

    // Social (NEW)
    social_links: '',

    // Content Strategy (NEW)
    content_work_type: '',
    monthly_content_pcs: '',
    customer_content_approval: '',
    seo_mastersheet: '',
    content_notes: '',
    hero_procedures: '',
    target_areas: '',
    tone_of_voice: '',
    preferred_content_type: '',

    // Links
    seo_master_sheet: '',
    google_drive_link: '',

    // IDs / System
    csv_account_id: '',
    access_id: '',
    strategy_id: '',
    monday_item_id: '',
    creation_log: '',

    // Google Sheets Integration
    google_sheet_id: '',
    google_sheet_url: '',
    google_sheet_shared_emails: ''
  });

  const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

  useEffect(() => {
    fetchSite();
    // Fetch tab counts upfront
    const fetchTabCounts = async () => {
      try {
        const [contactsRes, subsRes, domainsRes] = await Promise.all([
          api.get(`/sites/${siteId}/contacts`).catch(() => ({ data: [] })),
          api.get(`/sites/${siteId}/subscriptions`).catch(() => ({ data: [] })),
          api.get(`/sites/${siteId}/domains`).catch(() => ({ data: [] })),
        ]);
        setTabCounts({
          contacts: contactsRes.data.length,
          subscriptions: subsRes.data.length,
          domains: domainsRes.data.length,
        });
        // Pre-populate data so tabs don't refetch
        if (contactsRes.data.length) setContacts(contactsRes.data);
        if (subsRes.data.length) setSubscriptions(subsRes.data);
        if (domainsRes.data.length) setDomains(domainsRes.data);
      } catch (err) {
        console.error('Error fetching tab counts:', err);
      }
    };
    fetchTabCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  useEffect(() => {
    // Fetch contacts when Contacts tab is selected (index 1)
    if (tabValue === 1 && contacts.length === 0 && !loadingContacts) {
      fetchContacts();
    }
    // Fetch subscriptions when Subscriptions tab is selected (index 2)
    if (tabValue === 2 && subscriptions.length === 0 && !loadingSubscriptions) {
      fetchSubscriptions();
    }
    // Fetch domains when Domains tab is selected (index 3)
    if (tabValue === 3 && domains.length === 0 && !loadingDomains) {
      fetchDomains();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await api.get(`/sites/${siteId}/contacts`);
      setContacts(response.data);
      setTabCounts(prev => ({ ...prev, contacts: response.data.length }));
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoadingSubscriptions(true);
      const response = await api.get(`/sites/${siteId}/subscriptions`);
      setSubscriptions(response.data);
      setTabCounts(prev => ({ ...prev, subscriptions: response.data.length }));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  const fetchDomains = async () => {
    try {
      setLoadingDomains(true);
      const response = await api.get(`/sites/${siteId}/domains`);
      setDomains(response.data);
      setTabCounts(prev => ({ ...prev, domains: response.data.length }));
    } catch (error) {
      console.error('Error fetching domains:', error);
      setSnackbar({ open: true, message: 'Failed to load domains', severity: 'error' });
    } finally {
      setLoadingDomains(false);
    }
  };

  const handleOpenDomainDialog = (mode, domain = null) => {
    setDomainDialogMode(mode);
    if (mode === 'edit' && domain) {
      setEditingDomain(domain);
      setDomainFormData({
        domain_name: domain.domain_name || '',
        is_primary: domain.is_primary || false,
        cloudflare_api_token: '',
        hosting_expiry_date: domain.hosting_expiry_date || '',
        notes: domain.notes || '',
        final_url: domain.final_url || '',
        hosting_provider: domain.hosting_provider || '',
        dns_provider: domain.dns_provider || '',
        cms_detected: domain.cms_detected || '',
        registrar: domain.registrar || '',
      });
    } else {
      setEditingDomain(null);
      setDomainFormData({
        domain_name: '', is_primary: false,
        cloudflare_api_token: '', hosting_expiry_date: '', notes: '',
        final_url: '', hosting_provider: '', dns_provider: '', cms_detected: '', registrar: ''
      });
    }
    setDomainDialogOpen(true);
  };

  const handleSaveDomain = async () => {
    if (!domainFormData.domain_name.trim()) return;
    try {
      if (domainDialogMode === 'add') {
        await api.post(`/sites/${siteId}/domains`, domainFormData);
        setSnackbar({ open: true, message: 'Domain added successfully', severity: 'success' });
      } else {
        await api.put(`/sites/${siteId}/domains/${editingDomain.id}`, domainFormData);
        setSnackbar({ open: true, message: 'Domain updated successfully', severity: 'success' });
      }
      setDomainDialogOpen(false);
      setDomains([]);
      fetchDomains();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save domain';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleDeleteDomain = async () => {
    if (!deletingDomain) return;
    try {
      await api.delete(`/sites/${siteId}/domains/${deletingDomain.id}`);
      setSnackbar({ open: true, message: 'Domain deleted', severity: 'success' });
      setDeleteDialogOpen(false);
      setDeletingDomain(null);
      setDomains([]);
      fetchDomains();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to delete domain';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const handleScanDomain = async (domainId) => {
    setScanningDomainId(domainId);
    try {
      await api.post(`/sites/${siteId}/domains/${domainId}/scan`);
      setSnackbar({ open: true, message: 'Domain scan complete', severity: 'success' });
      setDomains([]);
      fetchDomains();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Scan failed';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setScanningDomainId(null);
    }
  };

  const handleCreateDomainSheet = async (domainId, domainName) => {
    try {
      setSnackbar({ open: true, message: `Creating Google Sheet for ${domainName}...`, severity: 'info' });
      const response = await api.post(`/sites/${siteId}/domains/${domainId}/create-google-sheet`);
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Google Sheet created successfully', severity: 'success' });
        setDomains([]);
        fetchDomains();
        if (response.data.google_sheet_url) {
          window.open(response.data.google_sheet_url, '_blank');
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to create Google Sheet';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    }
  };

  const fetchSite = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sites/${siteId}`);
      setSite(response.data);
      setOriginalSheetId(response.data.google_sheet_id || '');
      const data = response.data;
      const loadedData = {
        // Basic
        rootdomain: data.rootdomain || '',
        website_url: data.website_url || '',
        account_name: data.account_name || '',
        doctor_name: data.doctor_name || '',

        // Account
        account_status: data.account_status || '',
        priority: data.priority || '',
        total_spend: data.total_spend || '',
        account_id: data.account_id || '',
        account_id_unique: data.account_id_unique || '',
        item_id: data.item_id || '',

        // Status & Classification
        engagement_level: data.engagement_level || '',
        industry: data.industry || '',
        account_source: data.account_source || '',

        // Team
        assigned_cs: data.assigned_cs || '',
        cs: data.cs || '',
        strategist: data.strategist || '',
        writer: data.writer || '',
        seo_shirt_size: data.seo_shirt_size || '',
        seo_hours: data.seo_hours || '',
        monthly_update_hours: data.monthly_update_hours || '',

        // Billing
        quickbooks_account: data.quickbooks_account || '',
        phone: data.phone || '',
        billing_address: data.billing_address || '',
        billing_city: data.billing_city || '',
        billing_state: data.billing_state || '',
        billing_postal_code: data.billing_postal_code || '',

        // Business Review & Lifecycle
        br_status: data.br_status || '',
        br_date: data.br_date || '',
        start_date: data.start_date || '',
        cancellation_notification_date: data.cancellation_notification_date || '',
        cancellation_date: data.cancellation_date || '',
        opportunity_count: data.opportunity_count || '',

        // Services Notes
        services_notes: data.services_notes || '',

        // Technical
        cms: data.cms || '',
        divi: data.divi || '',
        domain_hosted_by_nkp: data.domain_hosted_by_nkp || '',
        hosting_details: data.hosting_details || '',
        preferred_url: data.preferred_url || '',
        looker_studio: data.looker_studio || '',

        // Analytics
        ga4_id: data.ga4_id || '',
        ga4_email: data.ga4_email || '',
        ga4_account_name: data.ga4_account_name || '',
        ga4_property_id: data.ga4_property_id || '',
        ga4_property_name: data.ga4_property_name || '',
        ga4_star: data.ga4_star || '',
        gtm_id: data.gtm_id || '',
        gtm_email: data.gtm_email || '',

        // Search Console
        gsc_domain: data.gsc_domain || '',
        gsc_prefix: data.gsc_prefix || '',
        gsc_url_prefix: data.gsc_url_prefix || '',
        verified_gsc_prefix: data.verified_gsc_prefix || '',
        verified_url_prefix: data.verified_url_prefix || '',
        gsc_email: data.gsc_email || '',

        // Domain
        registrar: data.registrar || '',
        registrar_username: data.registrar_username || '',

        // Social
        social_links: data.social_links || '',

        // Content Strategy
        content_work_type: data.content_work_type || '',
        monthly_content_pcs: data.monthly_content_pcs || '',
        customer_content_approval: data.customer_content_approval || '',
        seo_mastersheet: data.seo_mastersheet || '',
        content_notes: data.content_notes || '',
        hero_procedures: data.hero_procedures || '',
        target_areas: data.target_areas || '',
        tone_of_voice: data.tone_of_voice || '',
        preferred_content_type: data.preferred_content_type || '',

        // Links
        seo_master_sheet: data.seo_master_sheet || '',
        google_drive_link: data.google_drive_link || '',

        // IDs / System
        csv_account_id: data.csv_account_id || '',
        access_id: data.access_id || '',
        strategy_id: data.strategy_id || '',
        monday_item_id: data.monday_item_id || '',
        creation_log: data.creation_log || '',

        // Google Sheets
        google_sheet_id: data.google_sheet_id || '',
        google_sheet_url: data.google_sheet_url || '',
        google_sheet_shared_emails: data.google_sheet_shared_emails ?
          (() => {
            try {
              const emails = JSON.parse(data.google_sheet_shared_emails);
              return Array.isArray(emails) ? emails.join(', ') : data.google_sheet_shared_emails;
            } catch {
              return data.google_sheet_shared_emails;
            }
          })() : ''
      };
      setFormData(loadedData);
      setOriginalFormData(loadedData);
    } catch (error) {
      setError('Failed to load site details');
      console.error('Error fetching site:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'seo_hours' ? (value === '' ? '' : parseInt(value) || 0) : value
    }));

    // Check if Google Sheet ID was changed
    if (name === 'google_sheet_id') {
      const hasChanged = value.trim() !== originalSheetId.trim();
      setSheetVerified(!hasChanged);
      setVerificationError('');
      if (hasChanged) {
        setSuccess(''); // Clear any previous success messages
      }
    }
  };

  const getGoogleSheetsUrl = (sheetId) => {
    if (!sheetId) return '';
    return `https://docs.google.com/spreadsheets/d/${sheetId}`;
  };

  const handleVerifyGoogleSheet = async () => {
    if (!formData.google_sheet_id || !formData.google_sheet_id.trim()) {
      setVerificationError('Please enter a Google Sheet ID to verify');
      return;
    }

    setVerifyingSheet(true);
    setVerificationError('');
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/sites/${siteId}/verify-google-sheet`, {
        google_sheet_id: formData.google_sheet_id.trim()
      });

      const result = response.data;

      if (result.verified) {
        setSheetVerified(true);
        setSuccess(`✅ Google Sheet verified successfully! Title: "${result.sheet_title}"`);
        setOriginalSheetId(formData.google_sheet_id.trim()); // Update original ID so save button becomes active
      } else {
        setSheetVerified(false);
        setVerificationError(result.message);
        setError(`${result.message}\n\n${result.details}`);
      }
    } catch (error) {
      setSheetVerified(false);
      setVerificationError('Verification failed');
      setError(`Failed to verify Google Sheet: ${error.response?.data?.detail || error.message}`);
      console.error('Error verifying Google Sheet:', error);
    } finally {
      setVerifyingSheet(false);
    }
  };

  const hasUnsavedChanges = () => {
    if (!originalFormData) return false;
    const keys = new Set([...Object.keys(formData), ...Object.keys(originalFormData)]);
    for (const key of keys) {
      if (String(formData[key] ?? '') !== String(originalFormData[key] ?? '')) return true;
    }
    return false;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setSaveConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Prepare form data with proper JSON formatting for shared emails
      const submitData = { ...formData };
      
      // Convert comma-separated emails to JSON array
      if (submitData.google_sheet_shared_emails) {
        const emailsArray = submitData.google_sheet_shared_emails
          .split(',')
          .map(email => email.trim())
          .filter(email => email.length > 0);
        submitData.google_sheet_shared_emails = JSON.stringify(emailsArray);
      }

      // Auto-generate Google Sheet URL if ID is provided
      if (submitData.google_sheet_id && submitData.google_sheet_id.trim()) {
        submitData.google_sheet_url = getGoogleSheetsUrl(submitData.google_sheet_id.trim());
      } else if (!submitData.google_sheet_id) {
        submitData.google_sheet_url = '';
      }

      // Handle integer fields - convert empty strings to null for optional integers
      if (submitData.seo_hours === '') {
        submitData.seo_hours = null;
      }
      if (submitData.monthly_update_hours === '') {
        submitData.monthly_update_hours = null;
      }
      if (submitData.opportunity_count === '') {
        submitData.opportunity_count = null;
      }
      if (submitData.monthly_content_pcs === '') {
        submitData.monthly_content_pcs = null;
      }

      // Remove read-only fields that should not be sent to backend
      delete submitData.google_sheet_last_updated;
      delete submitData.plugin_installed;
      delete submitData.plugin_version;
      delete submitData.plugin_last_checked;
      delete submitData.date_created;
      delete submitData.date_updated;

      await api.put(`/sites/${siteId}`, submitData);
      setSuccess('Site updated successfully!');
      // Refresh site data
      fetchSite();
    } catch (error) {
      console.error('Full error object:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      let errorMessage = 'Unknown error occurred';
      
      if (error.response?.data?.detail) {
        // Handle validation errors from FastAPI
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.join('.')} - ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else {
          errorMessage = JSON.stringify(error.response.data.detail);
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(`Failed to update site: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCheckPlugin = async () => {
    setCheckingPlugin(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/sites/${siteId}/plugin-check`);
      const result = response.data;
      
      if (result.success) {
        if (result.plugin_installed) {
          setSuccess(`Plugin is installed! Version: ${result.plugin_version || 'Unknown'}`);
        } else {
          setError(`Plugin not found: ${result.error_message || 'Plugin endpoint not accessible'}`);
        }
        // Refresh site data to show updated plugin status
        fetchSite();
      } else {
        setError('Failed to check plugin status');
      }
    } catch (error) {
      setError('Network error while checking plugin status');
      console.error('Error checking plugin:', error);
    } finally {
      setCheckingPlugin(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!site) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Site not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/sites')}
          sx={{ mb: 2 }}
        >
          Back to Sites
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Language sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" component="h1" fontWeight="bold">
              Edit Site: {site.rootdomain}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mr: 2 }}>
                {site.website_url}
              </Typography>
              <Button
                size="small"
                startIcon={<Launch />}
                onClick={() => window.open(site.website_url, '_blank')}
                variant="outlined"
              >
                Visit Site
              </Button>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          {site.account_status && (
            <Chip 
              label={`Status: ${site.account_status}`} 
              color={site.account_status === 'Live' ? 'success' : 'default'} 
              size="small" 
            />
          )}
          {site.priority && (
            <Chip 
              label={`Priority: ${site.priority}`} 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
          )}
          {site.seo_shirt_size && (
            <Chip 
              label={`SEO Size: ${site.seo_shirt_size}`} 
              color="secondary" 
              variant="outlined" 
              size="small" 
            />
          )}
          {site.plugin_installed !== null && (
            <Chip 
              icon={site.plugin_installed ? <CheckCircle /> : <Error />}
              label={site.plugin_installed ? `Plugin: v${site.plugin_version || 'Unknown'}` : 'Plugin: Not Installed'} 
              color={site.plugin_installed ? 'success' : 'error'} 
              variant="outlined" 
              size="small" 
            />
          )}
        </Box>
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab label="Site Details" icon={<Settings />} iconPosition="start" />
          <Tab
            icon={<ContactMail />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Contacts
                {tabCounts.contacts !== null && (
                  <Chip label={tabCounts.contacts} size="small" sx={{
                    height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 700,
                    bgcolor: 'primary.main', color: 'white'
                  }} />
                )}
              </Box>
            }
          />
          <Tab
            icon={<Subscriptions />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Subscriptions
                {tabCounts.subscriptions !== null && (
                  <Chip label={tabCounts.subscriptions} size="small" sx={{
                    height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 700,
                    bgcolor: 'primary.main', color: 'white'
                  }} />
                )}
              </Box>
            }
          />
          <Tab
            icon={<Language />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Domains
                {tabCounts.domains !== null && (
                  <Chip label={tabCounts.domains} size="small" sx={{
                    height: 20, minWidth: 20, fontSize: '0.7rem', fontWeight: 700,
                    bgcolor: 'primary.main', color: 'white'
                  }} />
                )}
              </Box>
            }
          />
          <Tab label="Activity Logs" icon={<History />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <form onSubmit={handleFormSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Basic Information"
                avatar={<Settings color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Root Domain"
                      name="rootdomain"
                      value={formData.rootdomain}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account Name"
                      name="account_name"
                      value={formData.account_name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Doctor Name"
                      name="doctor_name"
                      value={formData.doctor_name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Account Details */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Account Details"
                avatar={<Assignment color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Account Status"
                      name="account_status"
                      value={formData.account_status}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Total Spend"
                      name="total_spend"
                      value={formData.total_spend}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Engagement Level"
                      name="engagement_level"
                      value={formData.engagement_level}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Industry"
                      name="industry"
                      value={formData.industry}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Account Source"
                      name="account_source"
                      value={formData.account_source}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Account ID (Salesforce)"
                      name="account_id"
                      value={formData.account_id}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Opportunity Count"
                      name="opportunity_count"
                      type="number"
                      value={formData.opportunity_count}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Information */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Billing Information"
                avatar={<Payment color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="QuickBooks Account"
                      name="quickbooks_account"
                      value={formData.quickbooks_account}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Billing Address"
                      name="billing_address"
                      value={formData.billing_address}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="billing_city"
                      value={formData.billing_city}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="State"
                      name="billing_state"
                      value={formData.billing_state}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Postal Code"
                      name="billing_postal_code"
                      value={formData.billing_postal_code}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Lifecycle & Dates */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Lifecycle & Dates"
                avatar={<DateRange color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="BR Status"
                      name="br_status"
                      value={formData.br_status}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="BR Date"
                      name="br_date"
                      type="date"
                      value={formData.br_date || ''}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      name="start_date"
                      type="date"
                      value={formData.start_date || ''}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cancellation Notification Date"
                      name="cancellation_notification_date"
                      type="date"
                      value={formData.cancellation_notification_date || ''}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cancellation Date"
                      name="cancellation_date"
                      type="date"
                      value={formData.cancellation_date || ''}
                      onChange={handleInputChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Services Notes"
                      name="services_notes"
                      value={formData.services_notes}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Team & SEO */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Team & SEO"
                avatar={<People color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Assigned CS"
                      name="assigned_cs"
                      value={formData.assigned_cs}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="CS"
                      name="cs"
                      value={formData.cs}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Strategist"
                      name="strategist"
                      value={formData.strategist}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Writer"
                      name="writer"
                      value={formData.writer}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>SEO Shirt Size</InputLabel>
                      <Select
                        name="seo_shirt_size"
                        value={formData.seo_shirt_size}
                        onChange={handleInputChange}
                        label="SEO Shirt Size"
                      >
                        {sizeOptions.map(size => (
                          <MenuItem key={size} value={size}>{size}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="SEO Hours"
                      name="seo_hours"
                      type="number"
                      value={formData.seo_hours}
                      onChange={handleInputChange}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Monthly Update Hours"
                      name="monthly_update_hours"
                      value={formData.monthly_update_hours}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Technical / Infrastructure */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Technical / Infrastructure"
                avatar={<Build color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="CMS"
                      name="cms"
                      value={formData.cms}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Divi"
                      name="divi"
                      value={formData.divi}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Domain Hosted by NKP"
                      name="domain_hosted_by_nkp"
                      value={formData.domain_hosted_by_nkp}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hosting Details"
                      name="hosting_details"
                      value={formData.hosting_details}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Preferred URL"
                      name="preferred_url"
                      value={formData.preferred_url}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Looker Studio"
                      name="looker_studio"
                      value={formData.looker_studio}
                      onChange={handleInputChange}
                      placeholder="https://lookerstudio.google.com/..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Content Strategy */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Content Strategy"
                avatar={<Article color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Content Work Type"
                      name="content_work_type"
                      value={formData.content_work_type}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Monthly Content Pieces"
                      name="monthly_content_pcs"
                      value={formData.monthly_content_pcs}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Customer Content Approval"
                      name="customer_content_approval"
                      value={formData.customer_content_approval}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SEO Mastersheet"
                      name="seo_mastersheet"
                      value={formData.seo_mastersheet}
                      onChange={handleInputChange}
                      placeholder="https://docs.google.com/..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Preferred Content Type"
                      name="preferred_content_type"
                      value={formData.preferred_content_type}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Hero Procedures"
                      name="hero_procedures"
                      value={formData.hero_procedures}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Target Areas"
                      name="target_areas"
                      value={formData.target_areas}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Tone of Voice"
                      name="tone_of_voice"
                      value={formData.tone_of_voice}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Content Notes"
                      name="content_notes"
                      value={formData.content_notes}
                      onChange={handleInputChange}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Domain & Social */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Domain & Social"
                avatar={<Public color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Registrar"
                      name="registrar"
                      value={formData.registrar}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Registrar Username"
                      name="registrar_username"
                      value={formData.registrar_username}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Social Links"
                      name="social_links"
                      value={formData.social_links}
                      onChange={handleInputChange}
                      multiline
                      rows={2}
                      placeholder="Facebook, Instagram, LinkedIn URLs..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Analytics & Tracking */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Analytics & Tracking"
                avatar={<Analytics color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 ID"
                      name="ga4_id"
                      value={formData.ga4_id}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Email"
                      name="ga4_email"
                      value={formData.ga4_email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Account Name"
                      name="ga4_account_name"
                      value={formData.ga4_account_name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Property ID"
                      name="ga4_property_id"
                      value={formData.ga4_property_id}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Property Name"
                      name="ga4_property_name"
                      value={formData.ga4_property_name}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GA4 Star"
                      name="ga4_star"
                      value={formData.ga4_star}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GTM ID"
                      name="gtm_id"
                      value={formData.gtm_id}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="GTM Email"
                      name="gtm_email"
                      value={formData.gtm_email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Search Console */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Google Search Console"
                avatar={<Language color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GSC Domain"
                      name="gsc_domain"
                      value={formData.gsc_domain}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GSC Prefix"
                      name="gsc_prefix"
                      value={formData.gsc_prefix}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GSC Email"
                      name="gsc_email"
                      value={formData.gsc_email}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="GSC URL Prefix"
                      name="gsc_url_prefix"
                      value={formData.gsc_url_prefix}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Verified GSC Prefix"
                      name="verified_gsc_prefix"
                      value={formData.verified_gsc_prefix}
                      onChange={handleInputChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Verified URL Prefix"
                      name="verified_url_prefix"
                      value={formData.verified_url_prefix}
                      onChange={handleInputChange}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* WordPress Plugin Status */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="WordPress Plugin Status"
                avatar={<Extension color="primary" />}
                action={
                  <Button
                    variant="outlined"
                    startIcon={checkingPlugin ? <CircularProgress size={20} /> : <Refresh />}
                    onClick={handleCheckPlugin}
                    disabled={checkingPlugin}
                    size="small"
                  >
                    {checkingPlugin ? 'Checking...' : 'Check Plugin'}
                  </Button>
                }
              />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {site.plugin_installed ? (
                        <CheckCircle sx={{ color: 'success.main', mr: 1 }} />
                      ) : (
                        <Error sx={{ color: 'error.main', mr: 1 }} />
                      )}
                      <Typography variant="body1" fontWeight="medium">
                        Status: {site.plugin_installed ? 'Installed' : 'Not Installed'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Version: {site.plugin_version || 'Unknown'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Last Checked: {site.plugin_last_checked ? 
                        new Date(site.plugin_last_checked).toLocaleString() : 
                        'Never'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Plugin:</strong> NKP Control Panel - Provides REST API endpoints for form submissions and user authentication.
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Links */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Links & Resources"
                avatar={<Launch color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="SEO Master Sheet"
                      name="seo_master_sheet"
                      value={formData.seo_master_sheet}
                      onChange={handleInputChange}
                      placeholder="https://docs.google.com/..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Google Drive Link"
                      name="google_drive_link"
                      value={formData.google_drive_link}
                      onChange={handleInputChange}
                      placeholder="https://drive.google.com/..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* System IDs */}
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="System IDs (Monday.com)"
                avatar={<Business color="primary" />}
              />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Monday Item ID"
                      name="monday_item_id"
                      value={formData.monday_item_id}
                      helperText="Account board item ID"
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: 'action.hover' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="CSV Account ID"
                      name="csv_account_id"
                      value={formData.csv_account_id}
                      helperText="Accounts 2.0 board item ID"
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: 'action.hover' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Access ID"
                      name="access_id"
                      value={formData.access_id}
                      helperText="Access List board item ID"
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: 'action.hover' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Strategy ID"
                      name="strategy_id"
                      value={formData.strategy_id}
                      helperText="Content Strategy board item ID"
                      InputProps={{ readOnly: true }}
                      sx={{ bgcolor: 'action.hover' }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Google Sheets Integration */}
          <Grid item xs={12}>
            <Card sx={{ overflow: 'hidden' }}>
              <CardHeader
                title="Google Sheets Integration"
                avatar={<TableChart color="primary" />}
              />
              <CardContent sx={{ overflow: 'hidden' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ minWidth: 0 }}>
                    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                      <TextField
                        fullWidth
                        label="Google Sheet ID"
                        name="google_sheet_id"
                        value={formData.google_sheet_id}
                        onChange={handleInputChange}
                        helperText={
                          !sheetVerified && formData.google_sheet_id !== originalSheetId
                            ? "⚠️ Google Sheet ID changed. Please verify before saving."
                            : sheetVerified
                              ? "✅ Google Sheet ID verified and ready to save"
                              : "Enter the Google Sheet ID"
                        }
                        placeholder="e.g. 1NmLvbXpezErCkdw..."
                        error={!sheetVerified && formData.google_sheet_id !== originalSheetId}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            overflow: 'hidden',
                          },
                          '& .MuiOutlinedInput-input': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ flexShrink: 0 }}>
                              {sheetVerified ? (
                                <CheckCircle sx={{ color: 'success.main' }} />
                              ) : formData.google_sheet_id !== originalSheetId ? (
                                <Warning sx={{ color: 'warning.main' }} />
                              ) : null}
                            </InputAdornment>
                          ),
                          endAdornment: formData.google_sheet_id && (
                            <InputAdornment position="end" sx={{ flexShrink: 0 }}>
                              <IconButton
                                size="small"
                                onClick={() => window.open(getGoogleSheetsUrl(formData.google_sheet_id), '_blank')}
                                title="Open in Google Sheets"
                                sx={{ color: 'primary.main' }}
                              >
                                <Launch fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      {formData.google_sheet_id !== originalSheetId && (
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={verifyingSheet ? <CircularProgress size={20} /> : <VerifiedUser />}
                            onClick={handleVerifyGoogleSheet}
                            disabled={verifyingSheet || !formData.google_sheet_id?.trim()}
                            sx={{ mr: 2 }}
                          >
                            {verifyingSheet ? 'Verifying...' : 'Verify Google Sheet'}
                          </Button>
                          {verificationError && (
                            <Chip 
                              label={verificationError} 
                              color="error" 
                              size="small" 
                              icon={<Error />}
                            />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
                    <TextField
                      fullWidth
                      label="Last Updated"
                      name="google_sheet_last_updated"
                      value={site.google_sheet_last_updated ? new Date(site.google_sheet_last_updated).toLocaleString() : 'Never'}
                      helperText="When the Google Sheet was last updated with form data"
                      InputProps={{
                        readOnly: true
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ minWidth: 0 }}>
                    <TextField
                      fullWidth
                      label="Google Sheet URL"
                      name="google_sheet_url"
                      value={formData.google_sheet_id ? getGoogleSheetsUrl(formData.google_sheet_id) : formData.google_sheet_url}
                      helperText={formData.google_sheet_id && !formData.google_sheet_url ?
                        "URL will be auto-generated from Sheet ID when saved" :
                        "Auto-generated from Sheet ID"
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          overflow: 'hidden'
                        },
                        '& .MuiOutlinedInput-input': {
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap'
                        }
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (formData.google_sheet_url || formData.google_sheet_id) && (
                          <InputAdornment position="end" sx={{ flexShrink: 0 }}>
                            <IconButton
                              size="small"
                              onClick={() => window.open(
                                formData.google_sheet_id ?
                                  getGoogleSheetsUrl(formData.google_sheet_id) :
                                  formData.google_sheet_url,
                                '_blank'
                              )}
                              title="Open in Google Sheets"
                              sx={{ color: 'primary.main' }}
                            >
                              <Launch fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ minWidth: 0 }}>
                    <TextField
                      fullWidth
                      label="Shared Email Addresses"
                      name="google_sheet_shared_emails"
                      value={formData.google_sheet_shared_emails}
                      onChange={handleInputChange}
                      helperText="Comma-separated email addresses with access to the Google Sheet"
                      placeholder="user1@example.com, user2@example.com"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  {!formData.google_sheet_id && (
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'info.light', 
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'info.main'
                      }}>
                        <Typography variant="body2" color="info.dark">
                          <strong>Note:</strong> Google Sheets integration allows you to automatically export form submissions to a shared spreadsheet. 
                          Use the "Open in Google Sheets" button on the Forms page to create and manage spreadsheets for this site.
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
              {!sheetVerified && (
                <Alert severity="warning" sx={{ mr: 'auto' }}>
                  Please verify the Google Sheet ID before saving changes
                </Alert>
              )}
              <Button
                onClick={() => {
                  if (hasUnsavedChanges()) {
                    setCancelConfirmOpen(true);
                  } else {
                    navigate('/sites');
                  }
                }}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                disabled={saving || !sheetVerified}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
        </form>
      </TabPanel>

      {/* Contacts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Card>
          <CardHeader
            title="Contacts"
            avatar={<ContactMail color="primary" />}
            subheader={`${contacts.length} contacts linked to this site`}
          />
          <CardContent>
            {loadingContacts ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : contacts.length === 0 ? (
              <Alert severity="info">
                No contacts linked to this site. Contacts are synced from Monday.com based on root domain.
              </Alert>
            ) : (
              <DataGrid
                rows={contacts}
                columns={[
                  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
                  { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
                  { field: 'phone', headerName: 'Phone', width: 130 },
                  { field: 'title', headerName: 'Title', width: 120 },
                  { field: 'account_name', headerName: 'Account', width: 150 },
                  {
                    field: 'do_not_call',
                    headerName: 'Do Not Call',
                    width: 110,
                    renderCell: (params) => (
                      params.value ? <Chip label="Yes" color="error" size="small" /> : <Chip label="No" color="success" size="small" />
                    )
                  },
                  { field: 'lead_source', headerName: 'Lead Source', width: 120 },
                  {
                    field: 'date_created',
                    headerName: 'Created',
                    width: 120,
                    valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : ''
                  }
                ]}
                autoHeight
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': { py: 1 },
                  minHeight: 400
                }}
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Subscriptions Tab */}
      <TabPanel value={tabValue} index={2}>
        <Card>
          <CardHeader
            title="Subscriptions"
            avatar={<Subscriptions color="primary" />}
            subheader={`${subscriptions.length} subscriptions linked to this site`}
          />
          <CardContent>
            {loadingSubscriptions ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : subscriptions.length === 0 ? (
              <Alert severity="info">
                No subscriptions linked to this site. Subscriptions are synced from Monday.com based on root domain.
              </Alert>
            ) : (
              <DataGrid
                rows={subscriptions}
                columns={[
                  { field: 'subscription_name', headerName: 'Subscription', flex: 1, minWidth: 250 },
                  {
                    field: 'status',
                    headerName: 'Status',
                    width: 100,
                    renderCell: (params) => (
                      <Chip
                        label={params.value || 'Unknown'}
                        color={params.value === 'Active' ? 'success' : 'default'}
                        size="small"
                      />
                    )
                  },
                  { field: 'product', headerName: 'Product', width: 150 },
                  { field: 'quickbooks', headerName: 'QuickBooks', width: 180 },
                  { field: 'launch_date', headerName: 'Launch Date', width: 120 },
                  { field: 'cancellation_date', headerName: 'Cancelled', width: 120 },
                  {
                    field: 'date_created',
                    headerName: 'Created',
                    width: 120,
                    valueFormatter: (params) => params.value ? new Date(params.value).toLocaleDateString() : ''
                  }
                ]}
                autoHeight
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': { py: 1 },
                  minHeight: 400
                }}
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Domains Tab */}
      <TabPanel value={tabValue} index={3}>
        <Card>
          <CardHeader
            title="Domains"
            avatar={<Language color="primary" />}
            subheader={`${domains.length} domain(s)`}
            action={
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => handleOpenDomainDialog('add')}
              >
                Add Domain
              </Button>
            }
          />
          <CardContent>
            {loadingDomains ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : domains.length === 0 ? (
              <Alert severity="info">
                No domains found for this site.
              </Alert>
            ) : (
              <DataGrid
                rows={domains}
                columns={[
                  { field: 'domain_name', headerName: 'Domain', flex: 1, minWidth: 180 },
                  {
                    field: 'is_active',
                    headerName: 'Status',
                    width: 80,
                    renderCell: (params) => (
                      params.value === null ? <Chip label="?" size="small" /> :
                      <Chip label={params.value ? 'Up' : 'Down'} color={params.value ? 'success' : 'error'} size="small" />
                    )
                  },
                  {
                    field: 'ssl_status',
                    headerName: 'SSL',
                    width: 90,
                    renderCell: (params) => {
                      if (!params.value) return <Chip label="N/A" size="small" />;
                      const colors = { valid: 'success', expired: 'error', missing: 'warning', error: 'default' };
                      return <Chip label={params.value} color={colors[params.value] || 'default'} size="small" />;
                    }
                  },
                  { field: 'cms_detected', headerName: 'CMS', width: 100, renderCell: (params) => params.value || '—' },
                  {
                    field: 'plugin_installed',
                    headerName: 'Plugin',
                    width: 80,
                    renderCell: (params) => (
                      params.value === null ? '—' :
                      <Chip label={params.value ? '✓' : 'No'} color={params.value ? 'success' : 'default'} size="small" />
                    )
                  },
                  {
                    field: 'cloudflare_status',
                    headerName: 'CF',
                    width: 70,
                    renderCell: (params) => (
                      params.value === null ? '—' :
                      <Chip label={params.value ? 'CF' : 'No'} color={params.value ? 'warning' : 'default'} size="small" />
                    )
                  },
                  {
                    field: 'is_primary',
                    headerName: 'Primary',
                    width: 80,
                    renderCell: (params) => (
                      <Chip label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} size="small" />
                    )
                  },
                  {
                    field: 'google_sheet_url',
                    headerName: 'Sheet',
                    width: 70,
                    renderCell: (params) => (
                      params.value ?
                        <IconButton size="small" onClick={() => window.open(params.value, '_blank')}>
                          <TableChart fontSize="small" color="success" />
                        </IconButton>
                      : '—'
                    )
                  },
                  {
                    field: 'last_scanned',
                    headerName: 'Scanned',
                    width: 130,
                    renderCell: (params) => {
                      if (!params.value) return 'Never';
                      const d = new Date(params.value);
                      const now = new Date();
                      const mins = Math.round((now - d) / 60000);
                      if (mins < 60) return `${mins}m ago`;
                      const hrs = Math.round(mins / 60);
                      if (hrs < 24) return `${hrs}h ago`;
                      return d.toLocaleDateString();
                    }
                  },
                  {
                    field: 'actions',
                    headerName: 'Actions',
                    width: 180,
                    sortable: false,
                    renderCell: (params) => (
                      <Box>
                        <IconButton size="small" onClick={() => handleOpenDomainDialog('edit', params.row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleScanDomain(params.row.id)} disabled={scanningDomainId === params.row.id}>
                          {scanningDomainId === params.row.id ? <CircularProgress size={16} /> : <Refresh fontSize="small" />}
                        </IconButton>
                        {!params.row.google_sheet_id && (
                          <IconButton size="small" color="primary" onClick={() => handleCreateDomainSheet(params.row.id, params.row.domain_name)} title="Create Google Sheet">
                            <TableChart fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" color="error" onClick={() => { setDeletingDomain(params.row); setDeleteDialogOpen(true); }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                  }
                ]}
                autoHeight
                pageSize={10}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': { py: 1 },
                  minHeight: 400
                }}
              />
            )}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Activity Logs Tab */}
      <TabPanel value={tabValue} index={4}>
        <LogsPanel siteId={siteId} rootDomain={site?.rootdomain} />
      </TabPanel>

      {/* Domain Add/Edit Dialog */}
      <Dialog open={domainDialogOpen} onClose={() => setDomainDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{domainDialogMode === 'add' ? 'Add Domain' : 'Edit Domain'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Domain Name" fullWidth required
            value={domainFormData.domain_name}
            onChange={(e) => setDomainFormData({ ...domainFormData, domain_name: e.target.value })}
            placeholder="e.g. example.com" sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={<Checkbox checked={domainFormData.is_primary} onChange={(e) => setDomainFormData({ ...domainFormData, is_primary: e.target.checked })} />}
            label="Primary Domain" sx={{ mt: 1 }}
          />

          {domainDialogMode === 'edit' && editingDomain && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                Scan Details
              </Typography>
              <Box sx={{ backgroundColor: 'action.hover', borderRadius: 1, p: 2, mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">IP Address</Typography>
                    <Typography variant="body2">{editingDomain.ip_address || '—'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">SSL Status</Typography>
                    <Typography variant="body2">
                      {editingDomain.ssl_status || '—'}
                      {editingDomain.ssl_expiry_date && ` (expires ${editingDomain.ssl_expiry_date})`}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Active</Typography>
                    <Typography variant="body2">
                      {editingDomain.is_active === null ? '—' : editingDomain.is_active ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Plugin</Typography>
                    <Typography variant="body2">
                      {editingDomain.plugin_installed === null ? '—' :
                        editingDomain.plugin_installed ? `Installed${editingDomain.plugin_version ? ` (v${editingDomain.plugin_version})` : ''}` : 'Not installed'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Cloudflare</Typography>
                    <Typography variant="body2">
                      {editingDomain.cloudflare_status === null ? '—' : editingDomain.cloudflare_status ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Domain Expiry</Typography>
                    <Typography variant="body2">{editingDomain.domain_expiry_date || '—'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Scan Status</Typography>
                    <Typography variant="body2">{editingDomain.scan_status || '—'}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Last Scanned</Typography>
                    <Typography variant="body2">
                      {editingDomain.last_scanned ? new Date(editingDomain.last_scanned).toLocaleString() : 'Never'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>
                Auto-Detected (editable for override)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField margin="dense" label="Final URL" fullWidth value={domainFormData.final_url}
                    onChange={(e) => setDomainFormData({ ...domainFormData, final_url: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField margin="dense" label="CMS" fullWidth value={domainFormData.cms_detected}
                    onChange={(e) => setDomainFormData({ ...domainFormData, cms_detected: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField margin="dense" label="Hosting Provider" fullWidth value={domainFormData.hosting_provider}
                    onChange={(e) => setDomainFormData({ ...domainFormData, hosting_provider: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField margin="dense" label="DNS Provider" fullWidth value={domainFormData.dns_provider}
                    onChange={(e) => setDomainFormData({ ...domainFormData, dns_provider: e.target.value })} />
                </Grid>
                <Grid item xs={6}>
                  <TextField margin="dense" label="Registrar" fullWidth value={domainFormData.registrar}
                    onChange={(e) => setDomainFormData({ ...domainFormData, registrar: e.target.value })} />
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, color: 'text.secondary' }}>
                Manual Entry
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField margin="dense" label="Cloudflare API Token" fullWidth type="password"
                    value={domainFormData.cloudflare_api_token}
                    onChange={(e) => setDomainFormData({ ...domainFormData, cloudflare_api_token: e.target.value })}
                    placeholder={editingDomain?.cloudflare_api_token_masked || 'Enter token'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField margin="dense" label="Hosting Expiry Date" fullWidth
                    value={domainFormData.hosting_expiry_date}
                    onChange={(e) => setDomainFormData({ ...domainFormData, hosting_expiry_date: e.target.value })}
                    placeholder="YYYY-MM-DD"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField margin="dense" label="Notes" fullWidth multiline rows={3}
                    value={domainFormData.notes}
                    onChange={(e) => setDomainFormData({ ...domainFormData, notes: e.target.value })}
                  />
                </Grid>
                {/* Google Sheets Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: 'text.secondary' }}>
                    Google Sheets
                  </Typography>
                  <Box sx={{ backgroundColor: 'action.hover', borderRadius: 1, p: 2 }}>
                    {editingDomain?.google_sheet_url ? (
                      <Grid container spacing={1}>
                        <Grid item xs={8}>
                          <Typography variant="caption" color="text.secondary">Sheet URL</Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            <a href={editingDomain.google_sheet_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                              {editingDomain.google_sheet_url}
                            </a>
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                          <Typography variant="body2">
                            {editingDomain.google_sheet_last_updated ? new Date(editingDomain.google_sheet_last_updated).toLocaleString() : 'Never'}
                          </Typography>
                        </Grid>
                        {editingDomain.google_sheet_shared_emails && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Shared With</Typography>
                            <Typography variant="body2">{editingDomain.google_sheet_shared_emails}</Typography>
                          </Grid>
                        )}
                        <Grid item xs={12} sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" color="success"
                            startIcon={<TableChart />}
                            onClick={() => window.open(editingDomain.google_sheet_url, '_blank')}
                            sx={{ textTransform: 'none', mr: 1 }}
                          >
                            Open Sheet
                          </Button>
                          <Button size="small" variant="outlined" color="primary"
                            startIcon={<Refresh />}
                            onClick={() => handleCreateDomainSheet(editingDomain.id, editingDomain.domain_name)}
                            sx={{ textTransform: 'none' }}
                          >
                            Refresh Data
                          </Button>
                        </Grid>
                      </Grid>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          No Google Sheet linked to this domain
                        </Typography>
                        <Button size="small" variant="contained" color="primary"
                          startIcon={<TableChart />}
                          onClick={() => handleCreateDomainSheet(editingDomain?.id, editingDomain?.domain_name)}
                          sx={{ textTransform: 'none' }}
                        >
                          Create Google Sheet
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDomainDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDomain} disabled={!domainFormData.domain_name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Domain Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Domain</DialogTitle>
        <DialogContent>
          Are you sure you want to delete domain '{deletingDomain?.domain_name}'?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteDomain}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for domain operations */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Changes"
        message="Do you want to save changes to this account?"
        onConfirm={handleSubmit}
        onCancel={() => setSaveConfirmOpen(false)}
        confirmLabel="Yes, Save"
        cancelLabel="No"
      />
      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Unsaved Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onConfirm={() => { setCancelConfirmOpen(false); navigate('/sites'); }}
        onCancel={() => setCancelConfirmOpen(false)}
        confirmLabel="Yes, Discard"
        cancelLabel="Keep Editing"
        confirmColor="error"
      />
    </Container>
  );
};

export default SiteEdit;